import {
  doc, getDoc, setDoc, getDocs, collection, query, where, documentId,
  increment, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { captureError, isTransientFirestoreError } from './monitoring';
import { todayStr } from '../utils/helpers';

function metaRef(uid: string) { return doc(db, 'progress', uid); }

function dayRef(uid: string, dateStr: string) {
  return doc(db, 'progress', uid, 'studyTime', dateStr);
}

const SESSION_GAP_MS = 5 * 60 * 1000;

export async function recordHeartbeat(uid: string): Promise<void> {
  try {
    const dateStr = todayStr();
    const ref = dayRef(uid, dateStr);
    const mRef = metaRef(uid);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);

      if (!snap.exists()) {
        tx.set(ref, {
          totalSeconds: 60,
          lastHeartbeat: serverTimestamp(),
          sessions: 1,
        });
        tx.set(mRef, { totalStudySeconds: increment(60), lastSeen: serverTimestamp() }, { merge: true });
      } else {
        const data = snap.data();
        const lastTs = data.lastHeartbeat?.toDate?.() || null;
        const gap = lastTs ? Date.now() - lastTs.getTime() : Infinity;
        const isNewSession = gap > SESSION_GAP_MS;
        const elapsed = isNewSession ? 60 : Math.min(Math.round(gap / 1000), 120);

        const updates: Record<string, unknown> = {
          totalSeconds: increment(elapsed),
          lastHeartbeat: serverTimestamp(),
        };
        if (isNewSession) updates.sessions = increment(1);

        tx.set(ref, updates, { merge: true });
        tx.set(mRef, { totalStudySeconds: increment(elapsed), lastSeen: serverTimestamp() }, { merge: true });
      }
    });
  } catch (err) {
    if (!isTransientFirestoreError(err as Error)) {
      captureError(err, { operation: 'recordHeartbeat', uid });
    }
    throw err;
  }
}

export interface StudyTimeSummary {
  totalSeconds: number;
  totalSessions: number;
}

export async function getStudyTimeForMonth(uid: string, year: number, month: number): Promise<StudyTimeSummary> {
  const prefix = `${year}-${String(month).padStart(2, '0')}`;
  const col = collection(db, 'progress', uid, 'studyTime');
  const q = query(col, where(documentId(), '>=', prefix + '-01'), where(documentId(), '<=', prefix + '-31'));
  const snap = await getDocs(q);

  let totalSeconds = 0;
  let totalSessions = 0;
  snap.docs.forEach((d) => {
    const data = d.data();
    totalSeconds += data.totalSeconds || 0;
    totalSessions += data.sessions || 0;
  });
  return { totalSeconds, totalSessions };
}

export async function getStudyTimeTotal(uid: string): Promise<StudyTimeSummary> {
  const mRef = metaRef(uid);
  const metaSnap = await getDoc(mRef);

  if (metaSnap.exists() && metaSnap.data().totalStudySeconds != null) {
    return { totalSeconds: metaSnap.data().totalStudySeconds, totalSessions: 0 };
  }

  const col = collection(db, 'progress', uid, 'studyTime');
  const snap = await getDocs(col);

  let totalSeconds = 0;
  let totalSessions = 0;
  snap.docs.forEach((d) => {
    const data = d.data();
    totalSeconds += data.totalSeconds || 0;
    totalSessions += data.sessions || 0;
  });

  if (totalSeconds > 0) {
    setDoc(mRef, { totalStudySeconds: totalSeconds }, { merge: true }).catch(() => {});
  }

  return { totalSeconds, totalSessions };
}

export function formatStudyTime(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 60) return '0 د';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours === 0) return `${minutes} د`;
  if (minutes === 0) return `${hours} س`;
  return `${hours} س ${minutes} د`;
}
